import { useTranslation } from 'react-i18next';
import { TrendingUp, Shield, Zap, Lock, Map, ShoppingBag, Award } from 'lucide-react';
import { translateHomeType, translateBuildPeriod, translateCountry } from '@/utils/translations';

const CONFIDENCE_MAP = {
  nl: { estimated: 'Geschat', high: 'Hoog', medium: 'Gemiddeld', low: 'Indicatief', likely: 'Waarschijnlijk' },
  en: { estimated: 'Estimated', high: 'High', medium: 'Medium', low: 'Indicative', likely: 'Likely' },
  de: { estimated: 'Geschätzt', high: 'Hoch', medium: 'Mittel', low: 'Indikativ', likely: 'Wahrscheinlich' },
  fr: { estimated: 'Estimé', high: 'Élevé', medium: 'Moyen', low: 'Indicatif', likely: 'Probable' },
};

export default function ReportPreview({ report }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const confMap = CONFIDENCE_MAP[lang] || CONFIDENCE_MAP.en;

  return (
    <div data-testid="report-preview">
      <div className="bg-white rounded-xl p-6 border border-border shadow-sm mb-6" data-testid="preview-summary">
        <h2 className="text-2xl font-medium text-primary mb-4" style={{ fontFamily: 'Outfit' }}>{t('report.previewTitle')}</h2>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div><p className="text-sm text-muted-foreground">{t('report.homeType')}</p><p className="font-medium">{translateHomeType(report.home_profile.home_type, t)}</p></div>
          <div><p className="text-sm text-muted-foreground">{t('report.buildPeriod')}</p><p className="font-medium">{translateBuildPeriod(report.home_profile.build_period, t)}</p></div>
          <div><p className="text-sm text-muted-foreground">{t('report.size')}</p><p className="font-medium">{report.home_profile.size}</p></div>
          <div><p className="text-sm text-muted-foreground">{t('report.country')}</p><p className="font-medium">{translateCountry(report.country, lang)}</p></div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 border border-border shadow-sm mb-6" data-testid="preview-priorities">
        <h2 className="text-2xl font-medium text-primary mb-1" style={{ fontFamily: 'Outfit' }}>
          {lang === 'nl' ? 'Uw eerste slimme bespaarstap' : 'Your first smart savings step'}
        </h2>
        <p className="text-xs text-muted-foreground mb-4">
          {lang === 'nl'
            ? 'Eerste logische stap voor uw woning. Indicatief — kan helpen om in de juiste volgorde te starten.'
            : 'First logical step for your home. Indicative — can help start in the right order.'}
        </p>
        <div className="space-y-3">
          {report.priorities.slice(0, 1).map((p) => (
            <div key={`p-${p.rank}`} className="p-4 border-l-4 border-primary bg-muted rounded-r-lg" data-testid="preview-first-step">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-semibold flex-shrink-0">{p.rank}</div>
                <div>
                  <h3 className="font-semibold">{p.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{p.description}</p>
                </div>
              </div>
            </div>
          ))}

          {/* Locked subsequent-steps placeholders — preview boundary */}
          {report.priorities.length > 1 && (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="preview-locked-steps">
              <div className="p-4 border border-dashed border-border bg-muted/40 rounded-lg flex items-start gap-3">
                <Lock className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground/80">
                    {lang === 'nl' ? 'Volgende logische stappen' : 'Next logical steps'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {lang === 'nl' ? 'Volgorde, kosten en vervolgstappen' : 'Order, costs, next steps'}
                  </p>
                </div>
              </div>
              <div className="p-4 border border-dashed border-border bg-muted/40 rounded-lg flex items-start gap-3">
                <Lock className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground/80">
                    {lang === 'nl' ? 'Energielabel-inzicht' : 'Energy-label insight'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {lang === 'nl' ? 'Indicatie waar uw woning richting beweegt' : 'Indication of where your home moves toward'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Early unlock CTA — preview boundary copy */}
      <div className="bg-primary/5 border-2 border-primary rounded-xl p-6 mb-6" data-testid="early-unlock-cta">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-primary mb-1" style={{ fontFamily: 'Outfit' }}>
            {lang === 'nl' ? 'Uw eerste bespaarstap is zichtbaar' : 'Your first savings step is visible'}
          </h3>
          <p className="text-sm text-foreground mb-4 max-w-md mx-auto">
            {lang === 'nl'
              ? 'Wilt u weten welke stappen daarna logisch zijn, wat ze ongeveer kosten en welke volgorde voor uw woning het meeste oplevert? Ontgrendel dan het volledige bespaarplan.'
              : "Want to know which steps make sense after this, roughly what they cost, and which order delivers the most for your home? Then unlock the complete savings plan."}
          </p>
          <ul className="text-sm text-foreground space-y-1.5 max-w-sm mx-auto text-left mb-5">
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">1.</span>
              <span>{lang === 'nl' ? 'Volledige volgorde van logische stappen' : 'Full sequence of logical steps'}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">2.</span>
              <span>{lang === 'nl' ? 'Kosten en wat u kunt verwachten per stap' : 'Costs and what to expect per step'}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">3.</span>
              <span>{lang === 'nl' ? 'Energielabel-inzicht en vervolgstappen (zelf doen, product, offerte)' : 'Energy-label insight and next steps (DIY, product, quote)'}</span>
            </li>
          </ul>
          <p className="text-[10px] text-muted-foreground">{lang === 'nl' ? 'Eenmalige betaling · geen abonnement · direct toegang' : 'One-time payment · no subscription · instant access'}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 border border-border shadow-sm mb-6" data-testid="preview-savings">
        <h2 className="text-2xl font-medium text-primary mb-4" style={{ fontFamily: 'Outfit' }}>{t('report.savingsDirection')}</h2>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="p-4 bg-muted rounded-lg text-center">
            <TrendingUp className="w-6 h-6 text-primary mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">{t('report.indicativeSavings')}</p>
            <p className="text-xl font-medium text-foreground mt-1">{report.estimated_savings.direction}</p>
          </div>
          <div className="p-4 bg-muted rounded-lg text-center">
            <Shield className="w-6 h-6 text-primary mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">{t('report.confidence')}</p>
            <p className="text-xl font-medium text-foreground mt-1">{confMap[report.estimated_savings.confidence] || report.estimated_savings.confidence}</p>
          </div>
          <div className="p-4 bg-muted rounded-lg text-center">
            <Zap className="w-6 h-6 text-primary mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">{t('report.measuresFound')}</p>
            <p className="text-xl font-medium text-foreground mt-1">{report.measures_count}</p>
          </div>
        </div>
      </div>

      {/* Preview snapshots — trust builders before paywall */}
      <div className="space-y-4 mb-6" data-testid="preview-snapshots">
        {/* Snapshot 1: Eerste stap preview */}
        <div className="bg-white rounded-xl p-5 border border-border shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xs font-bold">1</div>
            <h3 className="text-base font-medium text-primary" style={{ fontFamily: 'Outfit' }}>
              {lang === 'nl' ? 'Uw eerste stap' : 'Your first step'}
            </h3>
          </div>
          {report.priorities?.[0] && (
            <div className="bg-muted/50 rounded-lg p-3 mb-2">
              <p className="text-sm font-medium">{report.priorities[0].title}</p>
              <p className="text-xs text-muted-foreground mt-1">{report.priorities[0].description}</p>
            </div>
          )}
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground italic">
            <Lock className="w-3 h-3" />
            <span>{lang === 'nl' ? 'Kosten, besparing en uitvoering — in het volledige rapport' : 'Costs, savings and execution — in the full report'}</span>
          </div>
        </div>

        {/* Snapshot 2: Maatregelkaart preview */}
        <div className="bg-white rounded-xl p-5 border border-border shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <Map className="w-5 h-5 text-primary" />
            <h3 className="text-base font-medium text-primary" style={{ fontFamily: 'Outfit' }}>
              {lang === 'nl' ? 'Uw maatregelkaart' : 'Your measure overview'}
            </h3>
          </div>
          <div className="grid grid-cols-2 gap-2 mb-2">
            {report.priorities?.slice(0, 4).map((p, i) => (
              <div key={i} className="bg-muted/40 rounded px-3 py-2 text-xs text-muted-foreground">{p.title}</div>
            ))}
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground italic">
            <Lock className="w-3 h-3" />
            <span>{lang === 'nl' ? 'Volledige details, producten en offertes — in het volledige rapport' : 'Full details, products and quotes — in the full report'}</span>
          </div>
        </div>

        {/* Snapshot 3: Vervolgstap preview */}
        <div className="bg-white rounded-xl p-5 border border-border shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <ShoppingBag className="w-5 h-5 text-primary" />
            <h3 className="text-base font-medium text-primary" style={{ fontFamily: 'Outfit' }}>
              {lang === 'nl' ? 'Direct verder met producten of offertes' : 'Move forward with products or quotes'}
            </h3>
          </div>
          <div className="space-y-1.5 mb-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/40 rounded px-3 py-2">
              <div className="w-4 h-4 bg-green-100 rounded-full flex-shrink-0" />
              <span>{lang === 'nl' ? 'Aanbevolen producten per maatregel' : 'Recommended products per measure'}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/40 rounded px-3 py-2">
              <div className="w-4 h-4 bg-primary/10 rounded-full flex-shrink-0" />
              <span>{lang === 'nl' ? 'Offerte aanvragen bij uitvoerende partijen' : 'Request quotes from installation companies'}</span>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground italic">
            <Lock className="w-3 h-3" />
            <span>{lang === 'nl' ? 'Beschikbaar na ontgrendeling' : 'Available after unlocking'}</span>
          </div>
        </div>

        {/* Snapshot 4: Energielabel & woningwaarde */}
        <div className="bg-white rounded-xl p-5 border border-border shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <Award className="w-5 h-5 text-primary" />
            <h3 className="text-base font-medium text-primary" style={{ fontFamily: 'Outfit' }}>
              {lang === 'nl' ? 'Energielabel & woningwaarde' : 'Energy label & home value'}
            </h3>
          </div>
          <p className="text-xs text-muted-foreground mb-2 leading-relaxed">
            {lang === 'nl'
              ? 'Een energiezuinigere woning kan gunstig zijn voor uw energierekening, wooncomfort en mogelijk ook voor de waarde of verkoopbaarheid van uw woning. Powersure laat zien welke maatregelen in uw situatie logisch kunnen zijn richting een beter energielabel.'
              : 'A more energy-efficient home can benefit your energy bill, living comfort, and potentially your home\'s value. Powersure shows which measures may logically help toward a better energy label.'}
          </p>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground italic">
            <Lock className="w-3 h-3" />
            <span>{lang === 'nl' ? 'Volledig overzicht in het bespaarplan' : 'Full overview in the savings plan'}</span>
          </div>
        </div>
      </div>

      {/* Teaser blur */}
      <div className="relative bg-white rounded-xl p-6 border border-border shadow-sm overflow-hidden" data-testid="preview-teaser">
        <div className="filter blur-sm pointer-events-none">
          <h2 className="text-2xl font-medium text-primary mb-4" style={{ fontFamily: 'Outfit' }}>{t('report.fullRoadmap')}</h2>
          <div className="space-y-3">
            <div className="h-4 bg-muted rounded w-3/4" />
            <div className="h-4 bg-muted rounded w-1/2" />
            <div className="h-4 bg-muted rounded w-2/3" />
            <div className="h-20 bg-muted rounded" />
            <div className="h-4 bg-muted rounded w-1/2" />
          </div>
        </div>
        <div className="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-sm">
          <div className="text-center">
            <Shield className="w-10 h-10 text-primary mx-auto mb-3" />
            <p className="text-lg font-medium text-primary">{t('report.unlockToSee')}</p>
            <p className="text-sm text-muted-foreground mt-1">{t('report.unlockDescription')}</p>
          </div>
        </div>
      </div>

      {/* Trust signals for conversion */}
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="preview-trust">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Shield className="w-3.5 h-3.5 text-primary flex-shrink-0" />
          <span>{t('trust.independentAdvice')}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Shield className="w-3.5 h-3.5 text-primary flex-shrink-0" />
          <span>{t('trust.securePayment')}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Shield className="w-3.5 h-3.5 text-primary flex-shrink-0" />
          <span>{t('trust.oneTimePayment')}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Shield className="w-3.5 h-3.5 text-primary flex-shrink-0" />
          <span>{t('trust.productsRelevant')}</span>
        </div>
      </div>
    </div>
  );
}
